<template>
  <div>
    <b-row>
      <b-col>
        <b-table
          v-if="isLoaded"
          ref="commissions-report-table"
          :items="records"
          :fields="columns"
          :busy="isLoadingData"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.sortDesc"
          @sort-changed="handleSortingChanged"
          no-local-sorting
          show-empty
          responsive
          table
        >
          <template #cell(number)="data">
            <div>
              <b-link :to="referenceTypeLink(data.item.item_reference_type, data.item.item_reference_id)">
                {{ data.item.number }}
              </b-link>
            </div>
            <div class="text-secondary">
              {{ formattedReferenceType(data.item.item_reference_type) }}
            </div>
          </template>
          <template #cell(invoice_date)="data">
            {{ data.item.invoice_date | dateFormat }}
            <div class="text-secondary">
              <b-link
                class="text-secondary"
                :to="{ name: 'invoice', params: { id: data.item.invoice_id } }"
              >
                {{ data.item.invoice_number }}
              </b-link>
            </div>
          </template>
          <template #cell(service_id)="data">
            <text-with-popover
              v-if="data.item.item_description != data.item.service_name"
              :popover-id="`item-${data.item.id}-description`"
              :text="data.item.item_description"
            />
            <b-link
              class="font-size-sm text-prewrap"
              :to="{ name: 'service', params: { id: data.item.service_id } }"
            >
              {{ data.item.service_name }}
            </b-link>
          </template>
          <template #cell(item_amount)="data">
            <money-format
              :value="data.item.item_amount.amount"
              :currency-code="data.item.item_amount.currency"
              subunits-value
            />
          </template>
          <template #cell(commission_rate)="data">
            {{ data.item.commission_rate | percentFormatFilter }}
            <div class="text-secondary font-size-sm">
              Year {{ data.item.commission_year }}
            </div>
          </template>
          <template #cell(location_id)="data">
            <b-link
              :to="{ name: 'location', params: { id: data.item.location_id } }"
              v-b-tooltip.hover.window
              :title="`${data.item.street}, ${data.item.city}, ${data.item.state}, ${data.item.zip_code}`"
            >
              {{ data.item.store_number }}
            </b-link>
            <div class="text-nowrap text-secondary font-size-sm">
              {{ { city: data.item.city, state: data.item.state } | locationFormat }}
            </div>
            <b-link
              :to="{ name: 'client', params: { id: data.item.client_id } }"
              class="text-nowrap text-secondary font-size-sm"
            >
              {{ data.item.client_name }}
            </b-link>
          </template>
          <template #cell(commission_plan_id)="data">
            <div>
              <b-link :to="{ name: 'commission-plans', query: { text: data.item.plan_name } }">
                {{ data.item.plan_name }}
              </b-link>
            </div>
            <div class="text-nowrap text-secondary font-size-sm">
              {{ data.item | dateRangeFormat("plan_start_date", "plan_end_date", data.item.location.time_zone) }}
            </div>
            <b-link
              class="text-nowrap text-secondary font-size-sm"
              :to="{ name: 'user', params: { id: data.item.user_id } }"
            >
              {{ data.item.user_name }}
            </b-link>
          </template>
        </b-table>
        <sfs-skeleton-table
          v-else
          :rows="pagination.perPage"
          :columns="columns.length"
        />
      </b-col>
    </b-row>
    <b-row>
      <b-col
        cols="auto"
        :class="{ 'mr-auto': true, 'ml-auto': !showPerPage }"
      >
        <b-pagination
          v-if="showPagination"
          v-model="pagination.currentPage"
          :total-rows="pagination.maxResults > totalRows ? totalRows : pagination.maxResults"
          :per-page="pagination.perPage"
          last-class="d-none"
        />
      </b-col>
      <sfs-pagination-dropdown
        v-if="showPerPage"
        container-class="col-auto"
        :current-page="pagination.currentPage"
        :per-page="pagination.perPage"
        :total-rows="totalRows"
        @per-page-changed="onPerPageChanged"
      />
    </b-row>
  </div>
</template>

<script>
import urlMixin from '@/mixins/urlMixin';
import tableMixin from '@/mixins/tableMixin';
import translationMixin from '@/mixins/translationMixin';
import { COMMISSION_REPORT_TABLE_DEFAULT_COLUMNS } from '@/constants/reports';
import TextWithPopover from '@/components/shared/TextWithPopover.vue';

export default {
  name: 'CommissionReportsTable',
  mixins: [urlMixin, tableMixin, translationMixin],
  components: {
    TextWithPopover,
  },
  props: {
    columns: {
      type: Array,
      default: COMMISSION_REPORT_TABLE_DEFAULT_COLUMNS,
    },
  },
  methods: {
    getRecords() {
      this.isLoadingData = true;
      this.$analyticsAPI
        .getCommissionsReport(this.params)
        .then((response) => {
          const { total, records } = response;
          this.totalRows = total;
          this.records = records;
        })
        .catch(() => {
          this.records = [];
        })
        .finally(() => {
          this.loaded = true;
          this.isLoadingData = false;
          this.$emit('data-changed', this.records);
        });
    },
    formattedReferenceType(referenceType) {
      if (referenceType === 'WorkOrderCharge') {
        return  'Work Order';
      } else {
        return 'Recurring Charge';
      }
    },
    referenceTypeLink(referenceType, id) {
      if (referenceType === 'WorkOrderCharge') {
        return { name: 'work-order', params: { id: id } };
      } else {
        return { name: 'location', params: { id: id } };
      }
    },
  },
};
</script>
